// src/crm/conversations/MessageBubble.tsx
import type { Message } from '../../lib/types'
import { channelLabel, channelColor, directionLabel } from './channelMeta'

type Props = {
  message: Message
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

function AttachmentList({ attachments }: { attachments: Message['attachments'] }) {
  if (attachments.length === 0) return null
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginTop: '0.5rem' }}>
      {attachments.map((a) => (
        <a
          key={a.id}
          href={`/api/attachments/${a.id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: '0.75rem',
            color: 'var(--bronze)',
            background: 'var(--raised)',
            border: '1px solid var(--line)',
            borderRadius: '4px',
            padding: '0.125rem 0.5rem',
            textDecoration: 'none',
          }}
        >
          <span style={{ fontSize: '0.6875rem' }}>📎</span>
          {a.filename}
        </a>
      ))}
    </div>
  )
}

export function MessageBubble({ message }: Props) {
  const isOut = message.direction === 'out'
  const date = formatDate(message.sentAt ?? message.createdAt)

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isOut ? 'flex-end' : 'flex-start',
        gap: '0.25rem',
      }}
    >
      {/* Méta */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.75rem',
          color: 'var(--dim)',
        }}
      >
        {/* Badge canal */}
        <span
          data-testid="message-channel-badge"
          aria-label={channelLabel(message.channel)}
          style={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: channelColor(message.channel),
            background: 'var(--raised)',
            border: `1px solid ${channelColor(message.channel)}40`,
            borderRadius: '3px',
            padding: '0.0625rem 0.375rem',
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
          }}
        >
          {channelLabel(message.channel)}
        </span>
        {/* Badge direction */}
        <span
          aria-label={directionLabel(message.direction)}
          style={{
            fontSize: '0.6875rem',
            color: isOut ? 'var(--arancio)' : 'var(--muted)',
            fontWeight: 500,
          }}
        >
          {directionLabel(message.direction)}
        </span>
        <span>{date}</span>
        {message.status === 'draft' && (
          <span
            style={{
              fontSize: '0.6875rem',
              color: 'var(--dim)',
              fontStyle: 'italic',
            }}
          >
            Brouillon
          </span>
        )}
      </div>

      {/* Corps */}
      <div
        style={{
          maxWidth: '72%',
          background: isOut ? 'var(--elevated)' : 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: isOut ? '8px 2px 8px 8px' : '2px 8px 8px 8px',
          padding: '0.625rem 0.875rem',
        }}
      >
        {message.subject && (
          <div
            style={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: 'var(--fg)',
              marginBottom: '0.375rem',
            }}
          >
            {message.subject}
          </div>
        )}
        <p
          style={{
            fontSize: '0.875rem',
            color: 'var(--fg)',
            whiteSpace: 'pre-wrap',
            margin: 0,
            lineHeight: 1.55,
          }}
        >
          {message.body ?? ''}
        </p>
        <AttachmentList attachments={message.attachments} />
      </div>
    </div>
  )
}
